/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: Overview of component library statistics and recent activities
 * Style referenceFiles:
 * Design for: Desktop
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - View library stats: total components, recent updates, status breakdown
 * - Quick links to Create Component, Tokens, Changelog
 * - Navigate to: ComponentList, TokenOverview, Changelog
 * </page-design>
 */

import { useAtomValue } from 'jotai'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { LayoutSidebar } from '@/components/function/LayoutSidebar'
import * as DashboardStore from '@/stores/DashboardStore'
import { pageLinks } from '@/pageLinks'

const statusStyles: Record<string, string> = {
  published: 'bg-emerald-50 text-emerald-600',
  draft: 'bg-amber-50 text-amber-600',
  review: 'bg-sky-50 text-sky-600',
  archived: 'bg-gray-100 text-gray-500',
}
const statusLabels: Record<string, string> = {
  published: '已发布', draft: '草稿', review: '审核中', archived: '已归档',
}

function StatsGrid() {
  const { data, isPending } = useAtomValue(DashboardStore.refreshAtom)
  if (isPending || !data) {
    return (
      <div className="grid grid-cols-4 gap-6 mb-8">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="bg-white p-6 rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] animate-pulse">
            <div className="h-12 w-12 rounded-2xl bg-gray-100 mb-4" />
            <div className="h-8 bg-gray-100 rounded w-1/2 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-2/3" />
          </div>
        ))}
      </div>
    )
  }
  const items = [
    { label: '组件总数', value: data.stats.total, icon: 'lucide:layers', hc: 'group-hover:bg-[#1D263A]/5 group-hover:text-[#1D263A]' },
    { label: '草稿', value: data.stats.draft, icon: 'lucide:edit-3', hc: 'group-hover:bg-amber-50 group-hover:text-amber-600' },
    { label: '审核中', value: data.stats.review, icon: 'lucide:eye', hc: 'group-hover:bg-[#1D263A]/5 group-hover:text-[#1D263A]' },
    { label: '已发布', value: data.stats.published, icon: 'lucide:check-circle', hc: 'group-hover:bg-emerald-50 group-hover:text-emerald-600' },
  ]
  return (
    <div className="grid grid-cols-4 gap-6 mb-8">
      {items.map((s, i) => (
        <div key={i} className="bg-white p-6 rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-shadow duration-300 flex flex-col gap-4 group">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-gray-50 text-gray-500 group-hover:scale-110 transition-transform duration-300 ${s.hc}`}>
            <Icon icon={s.icon} className="text-2xl" />
          </div>
          <div>
            <h3 className="text-3xl font-bold tracking-tight">{s.value}</h3>
            <p className="text-sm text-gray-500 font-medium mt-1">{s.label}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

function RecentUpdates() {
  const { data } = useAtomValue(DashboardStore.refreshAtom)
  if (!data) return null
  return (
    <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold">最近更新</h2>
        <Link to={pageLinks.ComponentList()} className="text-sm font-medium text-[#1D263A] hover:text-[#151D2E] flex items-center gap-1">查看全部 <Icon icon="lucide:arrow-right" /></Link>
      </div>
      <div className="space-y-3">
        {data.recentUpdates.map(item => (
          <Link key={item.id} to={pageLinks.ComponentDetail(item.id)} className="flex items-center justify-between p-4 rounded-2xl hover:bg-gray-50 transition-colors group cursor-pointer">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 group-hover:bg-white group-hover:shadow-sm transition-all"><Icon icon="lucide:component" className="text-xl" /></div>
              <div>
                <div className="flex items-center gap-2"><span className="font-semibold">{item.name}</span><span className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-md">{item.version}</span></div>
                <span className="text-sm text-gray-500">{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('zh-CN') : ''}</span>
              </div>
            </div>
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${statusStyles[item.status] || ''}`}>{statusLabels[item.status] || item.status}</span>
          </Link>
        ))}
        {data.recentUpdates.length === 0 && <div className="text-center py-8 text-gray-400">暂无组件</div>}
      </div>
    </div>
  )
}

function ActivityFeed() {
  const { data } = useAtomValue(DashboardStore.refreshAtom)
  if (!data) return null
  return (
    <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-6 flex-1">
      <h2 className="text-lg font-bold mb-6">团队动态</h2>
      <div className="relative border-l-2 border-gray-100 ml-3 space-y-8 pb-4">
        {data.recentActivity.map(a => (
          <div key={a.id} className="relative pl-6">
            <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-white border-2 border-[#1D263A]/15 shadow-sm" />
            <p className="text-sm font-medium leading-snug">{a.publishedBy || '团队成员'} 发布了 v{a.version}</p>
            <span className="text-xs text-gray-400 mt-1 block">{a.createdAt ? new Date(a.createdAt).toLocaleDateString('zh-CN') : ''}</span>
          </div>
        ))}
        {data.recentActivity.length === 0 && <div className="pl-6 text-sm text-gray-400">暂无动态</div>}
      </div>
    </div>
  )
}

export default function Dashboard() {
  return (
    <div className="flex h-screen w-full bg-[#F8F9FA] font-['Noto_Sans_SC',sans-serif] text-[#1A1D25] overflow-hidden">
      <LayoutSidebar />
      <div className="flex-1 flex flex-col h-full overflow-hidden p-6 pb-0">
        <header className="flex items-center justify-between mb-8 px-2">
          <div><h1 className="text-2xl font-bold tracking-tight">概览</h1><p className="text-gray-500 mt-1">组件库统计和近期动态</p></div>
        </header>
        <div className="flex-1 overflow-y-auto pb-8 px-2">
          <StatsGrid />
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2"><RecentUpdates /></div>
            <div className="col-span-1 flex flex-col gap-6">
              <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-6 flex flex-col gap-4">
                <Link to={pageLinks.ComponentNew()} className="w-full bg-[#1D263A] hover:bg-[#151D2E] text-white font-medium py-3.5 px-4 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] hover:shadow-[0_6px_16px_rgba(29,38,58,0.35)] transition-all flex items-center justify-center gap-2"><Icon icon="lucide:plus" className="text-xl" /><span>新建组件</span></Link>
                <Link to={pageLinks.TokenOverview()} className="w-full bg-white border-2 border-gray-100 hover:border-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl transition-all flex items-center justify-center gap-2"><Icon icon="lucide:palette" className="text-xl text-gray-400" /><span>管理 Token</span></Link>
              </div>
              <ActivityFeed />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
